import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import FileUpload from "../../components/FileUpload";
import { adminApi } from "../../services/adminApi";

const TABS = ["Overview", "Documents"];
const DOC_CATEGORIES = [
  "vehicle_registration", "vehicle_inspection",
  "airport_badge", "other",
];

const STATUS_COLORS = {
  active: "bg-green-100 text-green-700 border-green-200",
  inactive: "bg-gray-100 text-gray-500 border-gray-200",
};
const EXPIRY_CHIP = {
  expired: "bg-red-100 text-red-700 border-red-200",
  expiring_soon: "bg-yellow-100 text-yellow-700 border-yellow-200",
  valid: "bg-green-100 text-green-700 border-green-200",
  no_expiry: "bg-gray-100 text-gray-500 border-gray-200",
};

function expiryStatus(doc) {
  if (!doc.expiryDate) return "no_expiry";
  const days = Math.ceil((new Date(doc.expiryDate) - new Date()) / 86400000);
  if (days < 0) return "expired";
  if (days <= 30) return "expiring_soon";
  return "valid";
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Edit
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Doc upload
  const [docFile, setDocFile] = useState(null);
  const [docForm, setDocForm] = useState({ documentCategory: "vehicle_registration", label: "", expiryDate: "" });
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const [confirm, setConfirm] = useState(null);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3500); };
  const err = (msg) => { setError(msg); setTimeout(() => setError(""), 5000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, dr] = await Promise.all([adminApi.getVehicle(id), adminApi.listDrivers()]);
      setData(d);
      setDrivers(dr.drivers || []);
      const v = d.vehicle;
      setEditForm({
        cabNumber: v.cabNumber || "",
        make: v.make, model: v.model, year: String(v.year),
        vin: v.vin || "", licensePlate: v.licensePlate,
        color: v.color || "", assignedDriverId: v.assignedDriverId || "",
        status: v.status || "active", notes: v.notes || "",
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const payload = { ...editForm, year: Number(editForm.year) };
      if (!payload.assignedDriverId) payload.assignedDriverId = null;
      await adminApi.updateVehicle(id, payload);
      setEditMode(false);
      flash("Vehicle updated.");
      load();
    } catch (e) { err(e.message); }
    finally { setSaving(false); }
  }

  async function handleUploadDoc(e) {
    e.preventDefault();
    if (!docFile) return;
    setUploadingDoc(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", docFile);
      fd.append("documentCategory", docForm.documentCategory);
      fd.append("label", docForm.label || docForm.documentCategory.replace(/_/g, " "));
      if (docForm.expiryDate) fd.append("expiryDate", docForm.expiryDate);
      await adminApi.uploadDoc("vehicle", id, fd);
      setDocFile(null);
      setDocForm({ documentCategory: "vehicle_registration", label: "", expiryDate: "" });
      flash("Document uploaded.");
      load();
    } catch (e) { err(e.message); }
    finally { setUploadingDoc(false); }
  }

  function handleDeleteDoc(docId) {
    setConfirm({
      title: "Delete document?",
      message: "This removes the file from storage permanently.",
      onConfirm: async () => {
        setConfirm(null);
        try { await adminApi.deleteDoc(docId); flash("Document deleted."); load(); }
        catch (e) { err(e.message); }
      },
    });
  }

  function handleDeleteVehicle() {
    setConfirm({
      title: "Delete this vehicle?",
      message: "This permanently removes the vehicle and all its compliance documents. This cannot be undone.",
      confirmLabel: "Yes, Delete Vehicle",
      onConfirm: async () => {
        setConfirm(null);
        try { await adminApi.deleteVehicle(id); navigate("/enrollme/admin/vehicles"); }
        catch (e) { err(e.message); }
      },
    });
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;
  if (!data) return <div className="text-red-500 p-8">{error}</div>;

  const { vehicle, docs = [] } = data;
  const assignedDriver = drivers.find((d) => d._id === String(vehicle.assignedDriverId));
  const expiredDocs = docs.filter((d) => expiryStatus(d) === "expired").length;
  const expiringSoon = docs.filter((d) => expiryStatus(d) === "expiring_soon").length;

  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1";

  return (
    <div>
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel || "Delete"}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <button onClick={() => navigate("/enrollme/admin/vehicles")} className="text-sm text-blue-600 hover:underline mb-2 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Vehicles
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{vehicle.year} {vehicle.make} {vehicle.model}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {vehicle.cabNumber && <>Cab #{vehicle.cabNumber} - </>}
            {vehicle.licensePlate}
            {vehicle.vin && <> · VIN: {vehicle.vin}</>}
            {vehicle.color && <> · {vehicle.color}</>}
          </p>
        </div>
        <span className={`mt-1 px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[vehicle.status] || STATUS_COLORS.active}`}>
          {vehicle.status}
        </span>
      </div>

      {/* Alerts */}
      {expiredDocs > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2 mb-3 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {expiredDocs} expired document{expiredDocs !== 1 ? "s" : ""} — action required.
        </div>
      )}
      {expiringSoon > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg px-4 py-2 mb-3 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {expiringSoon} document{expiringSoon !== 1 ? "s" : ""} expiring within 30 days.
        </div>
      )}

      {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2 mb-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 border border-green-200 rounded-lg px-4 py-2 mb-3 text-sm">{success}</div>}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${tab === t ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
            {t}
            {t === "Documents" && expiredDocs > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{expiredDocs}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === "Overview" && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-semibold text-gray-900">Vehicle Information</h2>
              {!editMode && (
                <button onClick={() => setEditMode(true)} className="text-sm text-blue-600 hover:underline font-medium">Edit</button>
              )}
            </div>

            {editMode ? (
              <div className="space-y-4 max-w-lg">
                <div>
                  <label className={labelCls}>Cab #</label>
                  <input
                    value={editForm.cabNumber}
                    onChange={(e) => setEditForm({ ...editForm, cabNumber: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                    className={inputCls}
                    inputMode="numeric"
                    pattern="\d{1,5}"
                    maxLength={5}
                    placeholder="123"
                  />
                  <p className="text-xs text-gray-400 mt-1">Cab # must be 1–5 digits and unique.</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className={labelCls}>Year</label>
                    <input value={editForm.year} onChange={(e) => setEditForm({ ...editForm, year: e.target.value })} className={inputCls} placeholder="2022" />
                  </div>
                  <div className="col-span-1">
                    <label className={labelCls}>Make</label>
                    <input value={editForm.make} onChange={(e) => setEditForm({ ...editForm, make: e.target.value })} className={inputCls} placeholder="Toyota" />
                  </div>
                  <div className="col-span-1">
                    <label className={labelCls}>Model</label>
                    <input value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} className={inputCls} placeholder="Camry" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>License Plate</label>
                    <input value={editForm.licensePlate} onChange={(e) => setEditForm({ ...editForm, licensePlate: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Color</label>
                    <input value={editForm.color} onChange={(e) => setEditForm({ ...editForm, color: e.target.value })} className={inputCls} placeholder="Black" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>VIN</label>
                  <input value={editForm.vin} onChange={(e) => setEditForm({ ...editForm, vin: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Status</label>
                  <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={inputCls}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Assigned Driver</label>
                  <select value={editForm.assignedDriverId} onChange={(e) => setEditForm({ ...editForm, assignedDriverId: e.target.value })} className={inputCls}>
                    <option value="">— Not assigned —</option>
                    {drivers.map((d) => (
                      <option key={d._id} value={d._id}>{d.firstName} {d.lastName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Notes</label>
                  <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} className={inputCls} />
                </div>
                <div className="flex gap-3 pt-1">
                  <button onClick={handleSave} disabled={saving}
                    className="bg-blue-600 text-white text-sm font-semibold px-6 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-60">
                    {saving ? "Saving…" : "Save Changes"}
                  </button>
                  <button onClick={() => setEditMode(false)} className="text-sm px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 max-w-lg">
                {[
                  ["Year", vehicle.year],
                  ["Cab #", vehicle.cabNumber || "—"],
                  ["Make", vehicle.make],
                  ["Model", vehicle.model],
                  ["License Plate", vehicle.licensePlate],
                  ["VIN", vehicle.vin || "—"],
                  ["Color", vehicle.color || "—"],
                  ["Status", vehicle.status],
                  ["Assigned Driver", assignedDriver ? `${assignedDriver.firstName} ${assignedDriver.lastName}` : "—"],
                  ["Added", new Date(vehicle.createdAt).toLocaleDateString()],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{k}</dt>
                    <dd className="text-sm text-gray-900 mt-0.5 capitalize">{v}</dd>
                  </div>
                ))}
                {vehicle.notes && (
                  <div className="col-span-2 border-t border-gray-100 pt-4 mt-1">
                    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</dt>
                    <dd className="text-sm text-gray-700">{vehicle.notes}</dd>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Danger zone */}
          <div className="bg-white rounded-xl border border-red-200 p-5">
            <h2 className="font-semibold text-red-700 mb-1">Danger Zone</h2>
            <p className="text-sm text-gray-500 mb-4">Permanently removes this vehicle and all its documents from the system.</p>
            <button onClick={handleDeleteVehicle} className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-5 py-2 rounded-lg transition">
              Delete Vehicle
            </button>
          </div>
        </div>
      )}

      {/* ── DOCUMENTS ── */}
      {tab === "Documents" && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Compliance Documents</h2>
              <span className="text-xs text-gray-400">{docs.length} file{docs.length !== 1 ? "s" : ""}</span>
            </div>
            {docs.length === 0 ? (
              <p className="px-5 py-8 text-sm text-gray-400 text-center">No documents on file. Upload one below.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">Label</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Category</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Expiry</th>
                    <th className="px-4 py-2.5 text-left font-semibold">Uploaded</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {docs.map((doc) => {
                    const status = expiryStatus(doc);
                    return (
                      <tr key={doc._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{doc.label}</td>
                        <td className="px-4 py-3 text-gray-500 capitalize">{doc.documentCategory?.replace(/_/g, " ")}</td>
                        <td className="px-4 py-3">
                          {doc.expiryDate ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${EXPIRY_CHIP[status]}`}>
                              {status === "expired" ? "Expired" : status === "expiring_soon" ? "Expiring soon" : "Valid"} · {new Date(doc.expiryDate).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">No expiry</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{new Date(doc.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-3 justify-end">
                            <button onClick={async () => { try { const { link } = await adminApi.getDocLink(doc._id); window.open(link, "_blank"); } catch (e) { err(e.message); } }}
                              className="text-blue-600 hover:underline text-xs font-medium">View</button>
                            <button onClick={() => handleDeleteDoc(doc._id)} className="text-red-500 hover:underline text-xs font-medium">Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Upload New Document</h3>
            <form onSubmit={handleUploadDoc} className="space-y-3 max-w-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Category</label>
                  <select value={docForm.documentCategory} onChange={(e) => setDocForm({ ...docForm, documentCategory: e.target.value })} className={inputCls}>
                    {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Label</label>
                  <input value={docForm.label} onChange={(e) => setDocForm({ ...docForm, label: e.target.value })}
                    placeholder="e.g. Registration 2026" className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Expiry Date (if applicable)</label>
                <input type="date" value={docForm.expiryDate} onChange={(e) => setDocForm({ ...docForm, expiryDate: e.target.value })} className={inputCls} />
              </div>
              <FileUpload onFile={setDocFile} label="Select document" />
              <button type="submit" disabled={!docFile || uploadingDoc}
                className="bg-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
                {uploadingDoc ? "Uploading…" : "Upload Document"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
